import { CalendarEvent } from 'angular-calendar';
import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { Dependencies } from '../../../store';
import { pluck } from '../../../store.decorator';
import { PayloadAction } from '../../../store.model';
import { CommitmentSummaryItem } from '../commitment-summary-item.model';
import {
    eventFormTransformer,
    EventParticipant,
    ScheduleEvent,
} from '../models/schedule-event.model';
import { ScheduleItem } from '../models/schedule.model';

import {
    ActionType,
    InitRetrieveEventAction,
    retrieveCommitmentResult,
    RetrieveCommitmentsAction,
    retrieveEvent,
    RetrieveEventsAction,
    retrieveEventsResult,
    SaveAction,
    saveResult,
    UpdateAction,
    updateResult,
} from './schedule-event.action';
import {
    scheduleEventFormValueReverseTransformer,
    scheduleEventUpdateFormValueReverseTransformer,
} from './schedule-event.form';
import {
    getEvent,
    listCommitments,
    listEvents,
    saveEvent,
    updateEvent,
} from './schedule-event.function';
import { RootState } from './schedule-event.state';

export interface ChainGroup<TPayload> {
    root: TPayload;

    currentCommitments?: CommitmentSummaryItem[];
    userId?: number;
    eventId?: number;
    startDate?: Date; // create event from coverage button will need a start date to calculate
    // end date and check if exist overlap with other events
    participants?: EventParticipant[];
    eventData?: ScheduleEvent;
    savedEvent?: ScheduleEvent;
    calendarEvents?: Array<CalendarEvent<ScheduleItem>>;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const retrieveCommitments = (
    action: ActionsObservable<
        PayloadAction<ActionType, RetrieveCommitmentsAction>
    >,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SCHEDULE__EVENT_RETRIEVE_COMMITMENTS),
        switchMap((act) => {
            const grp: ChainGroup<RetrieveCommitmentsAction> = {
                root: act.payload,
            };
            grp.userId = act.payload.userId;

            return of(null).pipe(
                listCommitments(deps, grp),
                map((result) =>
                    retrieveCommitmentResult(
                        {
                            userId: grp.root.userId,
                            commitments: result.currentCommitments,
                        },
                        act.storePath,
                    ),
                ),
                catchError((e) => {
                    return of(
                        retrieveCommitmentResult(
                            { userId: grp.root.userId, commitments: [] },
                            act.storePath,
                        ),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const retrieveEvents = (
    action: ActionsObservable<PayloadAction<ActionType, RetrieveEventsAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SCHEDULE__EVENT_RETRIEVE_EVENTS),
        switchMap((act) => {
            const grp: ChainGroup<RetrieveEventsAction> = { root: act.payload };
            grp.userId = act.payload.userId;
            grp.startDate = act.payload.startDate;

            return of(null).pipe(
                listEvents(deps, grp),
                map((result) =>
                    retrieveEventsResult(
                        {
                            userId: grp.root.userId,
                            startDate: grp.root.startDate,
                            calendarEvents: result.calendarEvents,
                        },
                        act.storePath,
                    ),
                ),
                catchError((e) => {
                    return of(
                        retrieveEventsResult(
                            {
                                userId: grp.root.userId,
                                startDate: grp.root.startDate,
                                calendarEvents: [],
                            },
                            act.storePath,
                        ),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const save = (
    action: ActionsObservable<PayloadAction<ActionType, SaveAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SCHEDULE__EVENT_SAVE),
        switchMap((act) => {
            const rstate = pluck<RootState>(state.value, act.storePath);
            const grp: ChainGroup<SaveAction> = { root: act.payload };
            grp.eventData = scheduleEventFormValueReverseTransformer(
                rstate.scheduleEventFormValue,
            );

            return of(null).pipe(
                saveEvent(deps, grp),
                map((result) => {
                    return saveResult(
                        {
                            event: result.savedEvent,
                        },
                        act.storePath,
                    );
                }),
                catchError((e) =>
                    of(saveResult({ event: null }, act.storePath)),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const initRetrieveEvent = (
    action: ActionsObservable<
        PayloadAction<ActionType, InitRetrieveEventAction>
    >,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SCHEDULE__EVENT_INIT_RETRIEVE_EVENT),
        switchMap((act) => {
            const grp: ChainGroup<InitRetrieveEventAction> = {
                root: act.payload,
            };

            return of(null).pipe(
                getEvent(deps, grp, act.payload.eventId),
                map((data) =>
                    retrieveEvent(
                        {
                            updateEvent: data.eventData,
                            scheduleEventFormValue: eventFormTransformer(
                                data.eventData,
                            ),
                        },
                        act.storePath,
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const update = (
    action: ActionsObservable<PayloadAction<ActionType, UpdateAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SCHEDULE__EVENT_UPDATE),
        switchMap((act) => {
            const rstate = pluck<RootState>(state.value, act.storePath);
            const grp: ChainGroup<SaveAction> = { root: act.payload };
            grp.eventData = scheduleEventUpdateFormValueReverseTransformer(
                rstate.scheduleEventFormValue,
            );
            return of(null).pipe(
                updateEvent(deps, grp),
                map((result) => {
                    return updateResult(
                        {
                            event: result.savedEvent,
                        },
                        act.storePath,
                    );
                }),
                catchError((e) =>
                    of(updateResult({ event: null }, act.storePath)),
                ),
            );
        }),
    );

export default [
    retrieveCommitments,
    retrieveEvents,
    save,
    initRetrieveEvent,
    update,
];
